import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
// import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import {
    CameraPriority,
    CameraWithInfo,
    ExternalBatteryPackMode,
    Location,
    PowerSupply,
    PurposeType,
    Status,
    WifiStatus,
} from '@EcamModel/model';
import EditIcon from '@assets/jsx-icon/EditIcon';
import ICDelete from '@assets/jsx-icon/ICDelete';
import IOSSwitch from '@components/IOSSwitch';
import PopUpWarning from '@components/PopUpWarning';
import PopUpCameraStatus from '@pages/cameras/details/components/PopUpCameraStatus';
import { pushError, pushSuccess } from '@components/toast';
import { cameraController, cameraPTZConfigureRequestHttpController } from '@controllers/index';
// import { cameraController, cameraPTZConfigureRequestHttpController } from '@controllers/index';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import { PTZPatrolMode } from '@EcamModel/model/PTZCamera';
import LocationsIcon from '@assets/drawer-icon/LocationsIcon';
import TechnicalDetailIcon from '@assets/jsx-icon/TechnicalDetailIcon';
import usePopUp from '@hooks/usePopUp';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import color from '@theme/Colors';
import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { externalPowerBattery } from '..';
import { BaseItem, StackRowSpace } from '../styles';
import CameraItemInfoDetails from './CameraItemInfoDetails';
import CameraThumbnail from './CameraThumbnail';
import ColumnDivider from './ColumnDivider';
import ListNoData from './NoDataCameras';
import TooltipDangerIconButton from './TooltipDangerIconButton';
import TooltipIconButton from './TooltipIconButton';
import BatteryGood from './icons/BatteryGood';
import BatteryLow from './icons/BatteryLow';
import BatteryMedium from './icons/BatteryMedium';
import ConnectedIcon from './icons/ConnectedIcon';
import DisconnectedIcon from './icons/DisconnectedIcon';

type Props = {
    cameraItem: CameraWithInfo;
    cameraType: CameraType;
    onChangeStatus: (id: number, checked: boolean) => void;
    onDeleteCamera: (id: number) => void;
    onChangeVerification(cameraId: number, checked: boolean): void;
};

export const purposesReadableMapping = {
    [PurposeType.Decommissioned]: 'Decommissioned',
    [PurposeType.OnHold]: 'On hold',
    [PurposeType.Prospective]: 'Prospective',
    [PurposeType.Deleted]: 'Deleted',
    [PurposeType.Testing]: 'Testing',
    [PurposeType.Live]: 'Live',
};

export const cameraPowerSuppliesReadableMapping: Record<PowerSupply, string> = {
    [PowerSupply.Kit180Wh]: '45W/180Wh',
    [PowerSupply.Hybrid]: 'Hybrid',
    [PowerSupply.Kit555Wh]: '100W/555Wh',
    [PowerSupply.Kit962Wh]: '100W/962Wh',
    [PowerSupply.Wired]: 'Wired',
    [PowerSupply.Kit1536Wh]: '1536Wh',
};

export const cameraPriorityReadableMapping: Record<CameraPriority, string> = {
    [CameraPriority.Low]: 'Low',
    [CameraPriority.Medium]: 'Medium',
    [CameraPriority.High]: 'High',
};

export const patrolModeReadableMapping: Record<PTZPatrolMode, string> = {
    [PTZPatrolMode.FullPatrol]: 'Full patrol',
    [PTZPatrolMode.VehicleOnlyPatrol]: 'Vehicle only patrol',
};

export const cameraTypeReadableMapping: Record<CameraType, string> = {
    [CameraType.Solar]: 'Solar',
    [CameraType.PTZ]: 'View 360',
    [CameraType.SP111]: 'SP111',
    [CameraType.PMC8266]: 'PMC8266',
};

const extendedCoverageColors = {
    green: '#009D4F',
    greenDark: '#1B7F3A',
    greenLight1: '#BFE6CE',
    greenLight2: '#7AB800',
    greenLight3: '#93D500',
    greenLight4: '#AEE0C2',
    bgLight: '#f7fceb',
    borderLight: '#d8f0a4',
};

const coverageStyles = {
    header: {
        bgGradientLight: extendedCoverageColors.greenLight3,
        bgGradientDark: extendedCoverageColors.greenLight2,
        borderGradientLight: extendedCoverageColors.greenLight1,
        borderGradientDark: extendedCoverageColors.greenLight4,
        text: color.textSecondary,
    },
    action: {
        text: extendedCoverageColors.green,
    },
    locationItem: {
        border: extendedCoverageColors.greenLight1,
        iconBg: extendedCoverageColors.greenLight2,
        iconBorder: extendedCoverageColors.greenLight3,
        iconFill: extendedCoverageColors.green,
    },
    zoneChip: {
        bg: extendedCoverageColors.greenLight3,
        text: extendedCoverageColors.green,
    },
};

export default function CameraItem({
    cameraItem,
    onChangeStatus,
    onDeleteCamera,
    onChangeVerification,
    cameraType,
}: Props) {
    const navigate = useNavigate();
    const [isChangeActive, setIsChangeActive] = useState(false);
    const isCameraNotAvailable = cameraItem.Status === Status.NotAvailable;
    const now = new Date();
    const deployDate = new Date(cameraItem.DeployedDate);
    const isBeforeDeployDate = moment(now).isBefore(deployDate);
    const [checked, setChecked] = useState(cameraItem.Status === Status.Active);
    const [isVerification, setIsVerification] = useState(cameraItem.IsVerificationsEnabled);

    const isMobile = useMediaQuery('(max-width:480px)');

    const handleChangeActive = (cameraId: number, checked: boolean, isActiveAllGeoZones?: boolean) => {
        setIsChangeActive(true);
        cameraController
            .changeStatus({ CameraId: cameraId, IsActiveAllGeoZones: isActiveAllGeoZones })
            .then(() => {
                onChangeStatus(cameraId, checked);
                pushSuccess('Change status successfully');
            })
            .catch(() => {})
            .finally(() => setIsChangeActive(false));
    };
    const handleChangeVerification = (cameraId: number, checked: boolean) => {
        setIsChangeActive(true);
        cameraController
            .updateConfigIsVerificationsEnabled({ CameraId: cameraId, Enable: checked })
            .then(() => {
                onChangeVerification(cameraId, checked);
                pushSuccess('Updated successfully');
            })
            .catch(() => {})
            .finally(() => setIsChangeActive(false));
    };

    const navigateToEdit = () => {
        navigate(`/cameras/${cameraItem.Id}/edit`);
    };

    const popUpWarning = usePopUp({
        onConfirm() {
            handleChangeActive(cameraItem.Id!, checked);
            popUpWarning.onClose();
        },
    });

    const popUpDeleteWarning = usePopUp({
        onConfirm() {
            onDeleteCamera(cameraItem.Id!);
            popUpDeleteWarning.onClose();
        },
    });

    const popUpWarningVerification = usePopUp({
        onConfirm() {
            popUpWarningVerification.onClose();
        },
    });

    console.log(`cameraItem:`, cameraItem);

    const getStatusColor = (status: PurposeType) => {
        const colorRender = (props: { color: string; bgcolor: string }) => ({
            colorText: props.color,
            bgColor: props.bgcolor,
        });

        const mappingColor = {
            [PurposeType.Prospective]: colorRender({
                color: color.secondary,
                bgcolor: '#E5F2FF',
            }),
            [PurposeType.Live]: colorRender({
                bgcolor: '#E5F5ED',
                color: '#009D4F',
            }),
            [PurposeType.Testing]: colorRender({
                bgcolor: '#CC00661A',
                color: '#CC0066',
            }),
            [PurposeType.Decommissioned]: colorRender({
                bgcolor: '#FCE8E5',
                color: '#E01B00',
            }),
            [PurposeType.OnHold]: colorRender({
                bgcolor: '#FFF8E5',
                color: '#FFB600',
            }),
        };
        return (
            mappingColor[status] ||
            colorRender({
                bgcolor: '#FCE8E5',
                color: '#E01B00',
            })
        );

        // switch (status) {
        //     case PurposeType.Prospective:
        //         return { colorText: color.secondary, bgColor: '#E5F2FF' };
        //     case PurposeType.Live:
        //         return { colorText: '#009D4F', bgColor: '#E5F5ED' };
        //     case PurposeType.Testing:
        //         return { colorText: '#CC0066', bgColor: '#CC00661A' };
        //     case PurposeType.Decommissioned:
        //         return { colorText: '#E01B00', bgColor: '#FCE8E5' };
        //     case PurposeType.OnHold:
        //         return { colorText: '#FFB600', bgColor: '#FFF8E5' };
        //     default:
        //         return { colorText: '#E01B00', bgColor: '#FCE8E5' };
        // }
    };
    const { colorText, bgColor } = getStatusColor(cameraItem.PurposeType!);

    const [open, setOpen] = useState<boolean>(false);
    const [selectImagePreview, setSelectImagePreview] = useState<string>('');
    const [isCoverageExpanded, setIsCoverageExpanded] = useState<boolean>(false);
    const [isTechnicalDetailsExpanded, setIsTechnicalDetailsExpanded] = useState<boolean>(false);

    const cameraExternalBatteryPack = cameraItem.Solar4gCamera?.Solar4gSysInfo?.ExternalBatteryPack;

    // Calculate totals for extended coverage
    const hasExtendedCoverage = cameraItem.ExtendedCoverage && cameraItem.ExtendedCoverage?.length > 0;
    const totalLocations = hasExtendedCoverage ? cameraItem.ExtendedCoverage?.length : 0;
    const totalZones = hasExtendedCoverage
        ? (cameraItem.ExtendedCoverage as Location[])?.reduce((acc, item) => acc + (item.Zones?.length ?? 0), 0)
        : 0;

    return (
        <Box
            sx={{
                width: '100%',
            }}
        >
            <BaseItem
                component={Stack}
                sx={{
                    flexDirection: 'column',
                }}
                width={'100%'}
            >
                <Stack sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }} gap={2}>
                    {cameraType === CameraType.Solar ? (
                        <CameraThumbnail blob={cameraItem.CameraPhotos?.[0]?.BlobName} geoZones={cameraItem.GeoZones || []} />
                    ) : (
                        <Box
                            sx={{
                                width: '208px',
                                textAlign: 'center',
                                margin: 'auto',
                                borderRadius: '4px',
                                backgroundColor: color.grey100,
                            }}
                        >
                            <ListNoData sx={{ minHeight: 120, border: 'none' }} />
                        </Box>
                    )}
                    <Stack flex={1} direction="column" gap={1}>
                        <StackRowSpace gap={{ xs: 0, sm: 2 }} flexWrap={'wrap'}>
                            <StackRowSpace gap={{ xs: 0, sm: 2 }}>
                                <Link to={`/cameras/${cameraItem.Id!.toString()}`} style={{ textDecoration: 'none' }}>
                                    <Typography
                                        variant="h5"
                                        sx={{ cursor: 'pointer', '&:hover': { color: color.priText } }}
                                    >
                                        {cameraItem.Name}
                                    </Typography>
                                </Link>
                                <Tooltip
                                    title="Average number of PCNs issued within the last 30 days."
                                    arrow
                                    placement="top"
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="center"
                                        display={{ xs: 'none', md: 'inherit' }}
                                        sx={{
                                            width: 25,
                                            height: 25,
                                            background: '#CC0066',
                                            borderRadius: 1,
                                            p: 2,
                                            ml: -1,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Typography color="#fff" textAlign="center">
                                            {cameraItem.PcnByCamera ?? 0}
                                        </Typography>
                                    </Stack>
                                </Tooltip>

                                {isCameraNotAvailable ? (
                                    <Tooltip
                                        arrow
                                        placement="top"
                                        title="The MAC address could not be found or is in the process of data synchronization."
                                    >
                                        <Chip
                                            size="small"
                                            sx={{
                                                borderRadius: '16px',
                                                height: 22,
                                                fontSize: 12,
                                                px: '5px',
                                                cursor: 'pointer',
                                                display: { xs: 'none', sm: 'inherit' },
                                            }}
                                            label={'Not available'}
                                        />
                                    </Tooltip>
                                ) : (
                                    <>
                                        {cameraType === CameraType.Solar && (
                                            <StackRowSpace
                                                gap={{ xs: 1, sm: 2 }}
                                                display={{ xs: 'none', md: 'inherit' }}
                                            >
                                                <TooltipIconButton
                                                    icon={
                                                        cameraItem.Solar4gCamera?.LastWifiStatus ===
                                                        WifiStatus.Connected ? (
                                                            <ConnectedIcon />
                                                        ) : (
                                                            <DisconnectedIcon />
                                                        )
                                                    }
                                                    tooltipTitle={
                                                        cameraItem.Solar4gCamera?.LastWifiStatus ===
                                                        WifiStatus.Connected
                                                            ? 'Connected'
                                                            : 'Not connected'
                                                    }
                                                />
                                                <TooltipIconButton
                                                    icon={getBatteryIcon(
                                                        cameraItem.Solar4gCamera?.LastBatteryStatus ?? 0
                                                    )}
                                                    tooltipTitle={`${cameraItem.Solar4gCamera?.LastBatteryStatus ?? 0}%`}
                                                />
                                            </StackRowSpace>
                                        )}
                                    </>
                                )}

                                {cameraItem.PurposeType !== null && (
                                    <Tooltip title="Camera's purpose" placement="top">
                                        <Chip
                                            size="small"
                                            sx={{
                                                borderRadius: '16px',
                                                fontSize: 12,
                                                px: '5px',
                                                background: bgColor,
                                                color: colorText,
                                                cursor: 'pointer',
                                                display: { xs: 'none', sm: 'inherit' },
                                            }}
                                            label={purposesReadableMapping[cameraItem.PurposeType!]}
                                        />
                                    </Tooltip>
                                )}

                                {cameraType === CameraType.Solar && !_.isNil(cameraExternalBatteryPack) && (
                                    <Tooltip
                                        title={
                                            cameraExternalBatteryPack === ExternalBatteryPackMode.ExternalPowerBattery
                                                ? 'Use external power for heating'
                                                : 'Use the internal battery for heating'
                                        }
                                        placement="top"
                                    >
                                        <Chip
                                            size="small"
                                            sx={{
                                                borderRadius: '16px',
                                                fontSize: 12,
                                                cursor: 'pointer',
                                                display: { xs: 'none', sm: 'inherit' },
                                            }}
                                            label={
                                                externalPowerBattery?.[cameraExternalBatteryPack as ExternalBatteryPackMode]
                                                    ?.Name
                                            }
                                        />
                                    </Tooltip>
                                )}
                            </StackRowSpace>
                            <Stack direction="row" gap={isMobile ? 1 : 2}>
                                <TooltipIconButton icon={<EditIcon />} tooltipTitle="Edit" onClick={navigateToEdit} />
                                <TooltipDangerIconButton
                                    icon={<ICDelete />}
                                    tooltipTitle="Delete"
                                    onClick={() => popUpDeleteWarning.setTrue()}
                                />
                                <PopUpWarning
                                    {...popUpDeleteWarning}
                                    message="Are you sure you want to delete this camera?"
                                />
                                <Tooltip
                                    title={
                                        isCameraNotAvailable && isBeforeDeployDate
                                            ? 'The camera will not be able to activate until the deployed date.'
                                            : ''
                                    }
                                    arrow
                                    placement="top"
                                >
                                    <Box display={isMobile ? 'none' : 'inherit'}>
                                        <IOSSwitch
                                            label={
                                                <Typography sx={{ minWidth: '55px' }}>
                                                    {cameraItem.Status === Status.Active ? 'Active' : 'Inactive'}
                                                </Typography>
                                            }
                                            iosSwitchProps={{
                                                checked: cameraItem.Status === Status.Active,
                                                onChange(event, checked) {
                                                    setChecked(checked);
                                                    popUpWarning.setTrue();
                                                },
                                                disabled: isChangeActive || isCameraNotAvailable || isBeforeDeployDate,
                                            }}
                                        />
                                        <PopUpWarning
                                            {...popUpWarning}
                                            message="Are you sure you want to change the status of this camera?"
                                        />
                                    </Box>
                                </Tooltip>
                            </Stack>
                        </StackRowSpace>

                        {(cameraItem.Zone?.Location?.Name || cameraItem.Zone?.PublicName) && !hasExtendedCoverage && (
                            <Stack direction={'row'} flexWrap={'wrap'}>
                                {cameraItem.Zone?.Location?.Name && (
                                    <Typography sx={{ color: color.secondary }}>
                                        Location: {cameraItem.Zone?.Location?.Name}
                                    </Typography>
                                )}
                                {cameraItem.Zone?.PublicName && (
                                    <>
                                        <ColumnDivider />
                                        <Typography sx={{ color: color.secondary }}>
                                            Zone: {cameraItem.Zone?.PublicName}
                                        </Typography>
                                    </>
                                )}
                            </Stack>
                        )}

                        {hasExtendedCoverage && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="flex-start"
                                spacing={0.5}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        opacity: 0.7,
                                    },
                                }}
                                onClick={() => setIsCoverageExpanded(!isCoverageExpanded)}
                            >
                                <Typography
                                    sx={{
                                        color: coverageStyles.action.text,
                                    }}
                                >
                                    Extended coverage: {totalLocations}{' '}
                                    {totalLocations === 1 ? 'location' : 'locations'} • {totalZones}{' '}
                                    {totalZones === 1 ? 'zone' : 'zones'}
                                </Typography>
                                {isCoverageExpanded ? (
                                    <MdOutlineKeyboardArrowUp size={18} color={coverageStyles.action.text} />
                                ) : (
                                    <MdOutlineKeyboardArrowDown size={18} color={coverageStyles.action.text} />
                                )}
                            </Stack>
                        )}

                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Box>
                                <Typography>Create verifications: </Typography>
                            </Box>
                            <Box>
                                <IOSSwitch
                                    label={
                                        <Typography sx={{ minWidth: '55px' }}>
                                            {!!cameraItem.IsVerificationsEnabled ? 'On' : 'Off'}
                                        </Typography>
                                    }
                                    iosSwitchProps={{
                                        checked: cameraItem.IsVerificationsEnabled,
                                        onChange(event, checked) {
                                            setIsVerification(checked);
                                            popUpWarningVerification.setTrue();
                                        },
                                        disabled: isChangeActive,
                                    }}
                                />
                                <PopUpCameraStatus
                                    open={popUpWarning.open}
                                    onClose={popUpWarning.onClose}
                                    isActivating={checked}
                                    onConfirm={(isActiveAllGeoZones) => {
                                        handleChangeActive(cameraItem.Id!, checked, isActiveAllGeoZones);
                                        popUpWarning.onClose();
                                    }}
                                />
                            </Box>
                            <PopUpWarning
                                {...popUpWarningVerification}
                                message="Are you sure you want to change the status of create verifications?"
                                onConfirm={() => {
                                    handleChangeVerification(cameraItem.Id!, isVerification!);
                                    popUpWarningVerification.onClose();
                                }}
                            />
                        </Stack>
                        <Typography>Camera type: {cameraTypeReadableMapping[cameraType]}</Typography>
                    </Stack>
                </Stack>

                <Collapse in={isCoverageExpanded}>
                    {/* Location and Zone List */}
                    <Box
                        sx={{
                            mt: 1.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                        }}
                    >
                        {(cameraItem.ExtendedCoverage as Location[])?.map((item, index) => (
                            <Stack
                                key={index}
                                spacing={1}
                                sx={{
                                    flex: '1 1 calc(33.333% - 16px)',
                                    minWidth: 0,
                                    bgcolor: extendedCoverageColors.bgLight,
                                    borderRadius: '8px',
                                    p: 1,
                                    border: `1px solid ${extendedCoverageColors.borderLight}`,
                                }}
                            >
                                {/* Location with green circle icon */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: color.grey200,
                                            borderRadius: '50%',
                                            border: `4px solid ${color.grey100}`,
                                        }}
                                    >
                                        <LocationsIcon width={18} height={18} fill={color.grey600} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: color.textSecondary,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {item.Name}
                                    </Typography>
                                </Stack>

                                {/* Zones as text */}
                                {item.Zones && item.Zones?.length > 0 && (
                                    <Stack direction="row" spacing={0.5} sx={{ pl: 3.5 }}>
                                        <Typography
                                            sx={{
                                                color: color.grey600,
                                                fontSize: '14px',
                                            }}
                                        >
                                            Zones:
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: color.grey600,
                                                fontSize: '14px',
                                            }}
                                        >
                                            {item.Zones && item.Zones.map((zone) => zone.PublicName).join(', ')}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        ))}
                    </Box>
                </Collapse>

                {/* Technical Details Section */}
                <Box
                    sx={{
                        bgcolor: color.grey100,
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                        mx: -2,
                        mb: -2,
                        mt: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0.5}
                        sx={{
                            cursor: 'pointer',
                            pb: isTechnicalDetailsExpanded ? 0 : 1.5,
                            pt: 1.5,
                            '&:hover': {
                                opacity: 0.7,
                            },
                        }}
                        onClick={() => setIsTechnicalDetailsExpanded(!isTechnicalDetailsExpanded)}
                    >
                        <TechnicalDetailIcon />
                        <Typography
                            sx={{
                                color: color.textPrimary,
                            }}
                            variant="body2"
                        >
                            Technical Details
                        </Typography>
                        {isTechnicalDetailsExpanded ? (
                            <KeyboardArrowUp sx={{ color: color.textPrimary, fontSize: 18 }} />
                        ) : (
                            <KeyboardArrowDown sx={{ color: color.textPrimary, fontSize: 18 }} />
                        )}
                    </Stack>
                    <Collapse in={isTechnicalDetailsExpanded}>
                        <Box sx={{ p: 2, m: 2 }} bgcolor={color.white} borderRadius={1}>
                            <CameraItemInfoDetails cameraItem={cameraItem} cameraType={cameraType} />
                        </Box>
                    </Collapse>
                </Box>

                {selectImagePreview && (
                    <Dialog
                        open={!!open}
                        onClose={() => {
                            setOpen(false);
                            setSelectImagePreview('');
                        }}
                        PaperProps={{
                            sx: {
                                minWidth: 1280,
                                minHeight: 720,
                            },
                        }}
                    >
                        <img style={{ width: 1280, height: 720 }} src={`${selectImagePreview}`} alt="Zoomable" />
                    </Dialog>
                )}
            </BaseItem>
        </Box>
    );
}

export const getBatteryIcon = (percent: number) => {
    if (percent > 50) return <BatteryGood width={18} height={18} />;
    if (percent > 20) return <BatteryMedium width={18} height={18} />;
    return <BatteryLow width={18} height={18} />;
};
